import { useMemo, useState } from "react";
import { MagnifyingGlassIcon, PlusIcon, StarIcon } from "@heroicons/react/24/outline";
import { getSupportedFieldTypes, getFieldTypeLabel } from "@/lib/fieldTypeCatalog";
import type { FieldTemplate } from "@/lib/wartungen";
import type { BuilderScope } from "./fieldModel";
import { beginDrag, endDrag } from "./dragState";
import { FIELD_TYPE_GROUPS, getFieldTypeHint, getFieldTypeIcon } from "./fieldTypeIcons";

export interface PaletteItem {
  id: string;
  template: FieldTemplate;
  fieldType: string;
  title: string;
  meta: string;
}

function buildPaletteItems(apiTemplates: FieldTemplate[]): PaletteItem[] {
  const items: PaletteItem[] = [];
  // API-Templates zuerst (tragen reichere Defaults: Label, Optionen, Gruppe).
  for (const t of apiTemplates) {
    const fieldType = String(t.field_type || "text");
    items.push({
      id: `tpl-${t.id}`,
      template: t,
      fieldType,
      title: String(t.default_label || t.template_key || fieldType),
      meta: `Vorlage · ${getFieldTypeLabel(fieldType)}`,
    });
  }
  // Builtin-Karten für jeden unterstützten Feldtyp (template_id 0).
  for (const entry of getSupportedFieldTypes()) {
    items.push({
      id: `builtin-${entry.type}`,
      template: {
        id: 0,
        template_key: `${entry.type}_standard`,
        field_type: entry.type,
        default_label: entry.label,
      },
      fieldType: entry.type,
      title: entry.label,
      meta: getFieldTypeHint(entry.type),
    });
  }
  return items;
}

interface PaletteGroup {
  id: string;
  label: string;
  items: PaletteItem[];
}

function groupItems(items: PaletteItem[]): PaletteGroup[] {
  const groups: PaletteGroup[] = [];
  const templates = items.filter((i) => i.id.startsWith("tpl-"));
  if (templates.length) {
    groups.push({ id: "templates", label: "Fertige Vorlagen", items: templates });
  }
  const builtinsByType = new Map(
    items.filter((i) => i.id.startsWith("builtin-")).map((i) => [i.fieldType, i]),
  );
  for (const group of FIELD_TYPE_GROUPS) {
    const groupItems = group.types
      .map((t) => builtinsByType.get(t))
      .filter((i): i is PaletteItem => Boolean(i));
    if (groupItems.length) groups.push({ id: group.id, label: group.label, items: groupItems });
  }
  return groups;
}

export function TemplatePalette({
  title,
  description,
  apiTemplates,
  scope,
  onAdd,
}: {
  title: string;
  description: string;
  apiTemplates: FieldTemplate[];
  scope: BuilderScope;
  onAdd?: (template: FieldTemplate) => void;
}) {
  const [query, setQuery] = useState("");
  const items = useMemo(() => buildPaletteItems(apiTemplates), [apiTemplates]);
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? items.filter(
          (i) =>
            i.title.toLowerCase().includes(q) ||
            i.meta.toLowerCase().includes(q) ||
            getFieldTypeLabel(i.fieldType).toLowerCase().includes(q),
        )
      : items;
    return groupItems(filtered);
  }, [items, query]);

  return (
    <aside className="mb-palette" aria-label={`Feld-Bibliothek ${scope}`}>
      <div className="mb-palette__header">
        <h3 className="text-sm font-semibold text-label">{title}</h3>
        <p className="mt-0.5 text-xs text-label-secondary">{description}</p>
        <label className="mb-palette__search">
          <MagnifyingGlassIcon className="mb-palette__search-icon" aria-hidden />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Feldtyp suchen …"
            aria-label="Feldtyp suchen"
          />
        </label>
      </div>
      <div className="mb-palette__body">
        {groups.length === 0 ? (
          <p className="mb-palette__empty">Keine Feldtypen gefunden.</p>
        ) : (
          groups.map((group) => (
            <section key={group.id} className="mb-palette__group">
              <h4 className="mb-palette__group-title">
                {group.id === "templates" ? <StarIcon className="mb-palette__group-icon" aria-hidden /> : null}
                {group.label}
              </h4>
              <div className="mb-palette__group-items">
                {group.items.map((item) => (
                  <PaletteCard key={item.id} item={item} scope={scope} onAdd={onAdd} />
                ))}
              </div>
            </section>
          ))
        )}
      </div>
    </aside>
  );
}

function PaletteCard({
  item,
  scope,
  onAdd,
}: {
  item: PaletteItem;
  scope: BuilderScope;
  onAdd?: (template: FieldTemplate) => void;
}) {
  const Icon = getFieldTypeIcon(item.fieldType);
  return (
    <article
      className="mb-template-card"
      draggable
      data-template-id={item.template.id}
      data-field-type={item.fieldType}
      title="Per Drag & Drop ins Formular ziehen"
      onDragStart={(e) => {
        beginDrag({ kind: "template", scope, templateId: item.id });
        e.dataTransfer.setData("application/x-maintenance-drag-type", "template");
        e.dataTransfer.setData("application/x-maintenance-template-id", item.id);
        e.dataTransfer.effectAllowed = "copy";
        e.currentTarget.classList.add("is-drag-ghost");
      }}
      onDragEnd={(e) => {
        endDrag();
        e.currentTarget.classList.remove("is-drag-ghost");
      }}
    >
      <span className="mb-template-card__icon" aria-hidden>
        <Icon />
      </span>
      <span className="mb-template-card__text">
        <span className="mb-template-card__title">{item.title}</span>
        {item.meta ? <span className="mb-template-card__meta">{item.meta}</span> : null}
      </span>
      {onAdd ? (
        <button
          type="button"
          className="mb-template-card__add"
          title="Ans Ende des Formulars anfügen"
          aria-label={`${item.title} hinzufügen`}
          onClick={(e) => {
            e.stopPropagation();
            onAdd(item.template);
          }}
        >
          <PlusIcon aria-hidden />
        </button>
      ) : null}
    </article>
  );
}

export { buildPaletteItems };
